import React, { useCallback, useEffect, useState, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { ExternalIcon } from '../../../../components/Link';
import { usePrevious } from '../../../../hooks/usePreviousHook';
import { SIZE } from '../../../typography/PlayText/PlayText';
import InfoPopout from './components/InfoPopout';
import {
    ContentInformation,
    ContentLink,
    ContentTitle,
    HiddenText,
    ToggleExpansionButton,
    ToggleIcon,
} from './ContentItemInfo.styles';

const LinkTypeResolver = ({ linkToDetailsPage, external, url, onContentInfoClick, children }) => {
    if (linkToDetailsPage) {
        return (
            <ContentLink
                isExternal={external}
                to={url}
                aria-hidden="true"
                tabIndex="-1"
                onAfterClick={onContentInfoClick}
            >
                {children}
            </ContentLink>
        );
    }

    return children;
};

const ContentItemInfo = (props) => {
    const {
        external,
        url,
        onContentInfoClick,
        heading,
        subHeading,
        longDescription,
        editModeActivated,
        index,
        markedAsDeleted,
        trackOnClick,
        expanded,
        toggleExpanded,
        expansionIconVisible,
        toggleExpansionIconVisible,
        linkToDetailsPage,
    } = props;
    const [hasExpanded, setHasExpanded] = useState(expanded);

    const iconText = expanded ? `Göm info om ${heading}` : `Visa mer info om ${heading}`;
    const a11yFallbackText = expanded ? `Visar mer info om ${heading}` : `Döljer mer info om ${heading}`;
    let a11yText = longDescription && expanded ? longDescription : a11yFallbackText;
    let a11yRef = useRef();
    const showInfo = expanded && !editModeActivated;

    const prevExpanded = usePrevious(expanded);

    useEffect(() => {
        // prevent info expand button from getting ugly focus border
        if (a11yRef.current && prevExpanded !== undefined && expanded !== prevExpanded) {
            a11yRef.current.focus();
        }

        if (expanded && !hasExpanded) {
            setHasExpanded(true);
        }
    }, [a11yText, expanded, hasExpanded, prevExpanded]);

    const toggle = useCallback(() => toggleExpanded((state) => !state), [toggleExpanded]);

    const toggleVisibility = (e) => {
        e.preventDefault();
        toggle();
    };

    return (
        <>
            <ContentInformation
                key="meta-transition"
                data-rt="content-item-content"
                aria-hidden={markedAsDeleted ? 'true' : null}
                hidden={markedAsDeleted}
                hasExpanded={hasExpanded}
                onMouseEnter={(e) => !editModeActivated && toggleExpansionIconVisible(true)}
                onMouseLeave={(e) => !editModeActivated && toggleExpansionIconVisible(false)}
            >
                <LinkTypeResolver
                    external={external}
                    url={url}
                    onContentInfoClick={onContentInfoClick}
                    linkToDetailsPage={linkToDetailsPage}
                >
                    <ContentTitle data-rt="content-item-title" expanded={expanded} size={SIZE.SMALL}>
                        {external ? <ExternalIcon isExternal={external}>{heading}</ExternalIcon> : heading}
                    </ContentTitle>
                    {subHeading && subHeading}
                </LinkTypeResolver>
                {!editModeActivated && (expansionIconVisible || expanded) && (
                    <>
                        <ToggleExpansionButton
                            type="button"
                            onClick={toggleVisibility}
                            expansionIconVisible={expansionIconVisible}
                            onBlur={() => {
                                toggleExpansionIconVisible(false);
                            }}
                        >
                            <ToggleIcon aria-hidden="true" expanded={expanded} />
                            <HiddenText>{iconText}</HiddenText>
                        </ToggleExpansionButton>
                        <HiddenText ref={a11yRef} tabIndex="-1">
                            {a11yText}
                        </HiddenText>
                    </>
                )}
            </ContentInformation>
            <CSSTransition unmountOnExit={true} in={showInfo} timeout={{ enter: 0, exit: 4500 }}>
                <InfoPopout
                    external={external}
                    url={url}
                    index={index}
                    description={longDescription}
                    trackOnClick={trackOnClick}
                />
            </CSSTransition>
        </>
    );
};

export default ContentItemInfo;
